// Client for Training Mode via /api/admin/action (single serverless fn on Vercel hobby limits).
import { ADMIN_ACTION, trainingFetch, type FetchResult } from "@/lib/training/trainingApiClientCore";
import type { RoleplayLine, RoleplayScore } from "@/lib/training/trainingApiClientRoleplay";

export type { FetchResult } from "@/lib/training/trainingApiClientCore";
export * from "@/lib/training/trainingApiClientRoleplay";

export type ReviewQueueSession = {
  id: string;
  user_id: string;
  traineeLabel?: string;
  scenario_key: string;
  messages: RoleplayLine[];
  score: RoleplayScore | Record<string, unknown> | null;
  summary: string | null;
  overall_score: number | null;
  created_at: string;
  message_count: number;
  review_status?: string;
};

export async function fetchTrainingProgress(organizationId: string): Promise<
  FetchResult<{
    ok: true;
    totalSessions: number;
    bestOverallScore: number;
    lastSessionAt: string | null;
    byScenario: Record<string, number>;
    pendingHumanReviews?: number;
    recent: Array<{
      id: string;
      scenario_key: string;
      overall_score: number | null;
      summary: string | null;
      created_at: string;
      message_count: number;
      review_status?: string;
    }>;
  }>
> {
  const sp = new URLSearchParams();
  sp.set("action", "training-session");
  sp.set("organizationId", organizationId);
  return trainingFetch(`${ADMIN_ACTION}?${sp}`, { method: "GET" });
}

export async function fetchTrainingReviewQueue(organizationId: string): Promise<
  FetchResult<{ ok: true; reviewQueue: ReviewQueueSession[] }>
> {
  const sp = new URLSearchParams();
  sp.set("action", "training-session");
  sp.set("organizationId", organizationId);
  sp.set("reviewQueue", "1");
  return trainingFetch(`${ADMIN_ACTION}?${sp}`, { method: "GET" });
}

export async function submitTrainingHumanReview(body: {
  organizationId: string;
  reviewSessionId: string;
  reviewStatus: "reviewed" | "skipped";
  humanModelScores?: { grounding: number; tone: number; accuracy: number };
  humanLearnerScores?: {
    clarity: number;
    answersCustomer: number;
    followUpQuestions: number;
    openEndedQuestions: number;
  };
  reviewNote?: string | null;
  /** When false, keep transcript after review (default: archive and slim). */
  finalizeArchive?: boolean;
}): Promise<FetchResult<{ ok: true }>> {
  const sp = new URLSearchParams();
  sp.set("action", "training-session");
  return trainingFetch(`${ADMIN_ACTION}?${sp}`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}
